/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
/**
 *
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.11.0
 * @since 1.0.0
 */

/**
 * @description loginRecovery - iterator function to request a password reset.
 * @param {string} payload - user login
 * @returns {SagaIterator}
 */
import { SagaIterator } from '@redux-saga/core';
import { call, put, select } from 'redux-saga/effects';

import { API_HOSTNAME, RECOVERY_LOGIN } from '../../../modules/app/Api';
import { appHistoryPush } from '../../../modules/app/appHistory';
import { makeSelectTranslation } from '../../../modules/internationalization/intSelectors';
import { ITranslation } from '../../../modules/internationalization/types';
import { closeNotificationAction, openNotification } from '../../../modules/notification/notificationActions';
import { ENotificationLevelTypes, ENotificationTypes } from '../../../modules/notification/types/notificationTypes';
import { RoutePath } from '../../../routes/constants/routesConstants';
import { requestSaga } from '../../../sagas/requestSaga';
import {
    loginRecoveryAnswerFailed,
    loginRecoveryAnswerSuccessful,
    loginRecoveryFailed,
    loginRecoveryReset,
    loginRecoverySuccessful,
} from '../actions/recoveryActions';
import { ILoginRecoveryAnswerRequest, ILoginRecoveryAnswerResult, ILoginRecoveryAnswerState } from '../types/authTypes';

export function* loginRecovery(action: { payload: string; type: string }): SagaIterator {
    try {
        const recovery = yield call(requestSaga, API_HOSTNAME + RECOVERY_LOGIN + `/${action.payload}`, 'GET');
        yield put(loginRecoverySuccessful(recovery));
        yield put(closeNotificationAction());
    } catch (error: any) {
        yield put(loginRecoveryFailed(error));
    }
}

/**
 * recoveryAnswer - iterator function, to answer the password reset question.
 * @param {{payload: ILoginRecoveryAnswerRequest; type: string}} action
 * @returns {SagaIterator}
 */
export function* recoveryAnswer(action: { payload: ILoginRecoveryAnswerRequest; type: string }): SagaIterator {
    const translation = yield select(makeSelectTranslation());
    try {
        const answer: ILoginRecoveryAnswerState = yield call(
            requestSaga,
            API_HOSTNAME + RECOVERY_LOGIN + `/${action.payload.username}`,
            'POST',
            action.payload,
        );
        if (!answer.problem) {
            yield put(loginRecoveryAnswerSuccessful(answer));
            yield put(openNotification({
                message: getAnswerMessage(answer.result, translation),
                type: ENotificationTypes.AUTH,
                level: ENotificationLevelTypes.SUCCESS,
            }));
            yield call(appHistoryPush, RoutePath.login);
        } else {
            yield put(openNotification({
                message: translation.pageAuth.notice.incorrectAnswer,
                type: ENotificationTypes.AUTH,
                level: ENotificationLevelTypes.ERROR,
            }));
        }
    } catch (error: any) {
        yield put(loginRecoveryAnswerFailed(error));
    }
}

export function* recoveryReset(): SagaIterator {
    yield put(closeNotificationAction());
    yield put(loginRecoveryReset());
}

function getAnswerMessage(result: ILoginRecoveryAnswerResult, translation: ITranslation): string {
    if (result.sentByEmail && result.sentBySms) {
        return translation.pageAuth.notice.passwordRecoverBoth;
    }
    if (result.sentBySms) {
        return translation.pageAuth.notice.passwordRecoverPhone;
    }
    if (result.sentByEmail) {
        return translation.pageAuth.notice.passwordRecoverMail;
    }
    return '';
}
